import { useState } from 'react';
import { cn } from '@/lib/utils';
import { trpc } from '@/data-loaders';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui';
import { Check } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@curtin-dxp/curtin-ui';
import { AlertModal, ChevronIcon, DeleteButton } from '@/components/Common';
import { useAwmsStore } from '@/store/AwmsStore';
import { useHandleError, useElementSize, useSearchAcademic, useHandleSuccess } from '@/hooks';
import { UnitWorkloadAssignmentProps } from './UnitWorkloadAssignment';
import { Loader } from '@/components/Common/Loader';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useIsAdmin } from '@/hooks/use-is-admin';

export const UnitWorkloadAcademicAssignment = ({
  unitWorkloadDetails,
  unitWorkloadCampuses,
  assignedAcademics,
}: UnitWorkloadAssignmentProps) => {
  const { id: unitWorkloadId } = unitWorkloadDetails;
  const handleError = useHandleError();
  const selectedCampusId = useAwmsStore((state) => state.selectedCampusId);
  const selectedSchoolId = useAwmsStore((state) => state.selectedSchoolId);
  const activeYear = useAwmsStore((state) => state.activeYear);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [commandInputValue, setCommandInputValue] = useState<string>('');
  const [alert, setAlert] = useState<boolean>(false);
  const [cannotRemoveAlert, setCannotRemoveAlert] = useState<boolean>(false);
  const [triggerRef, { width: triggerWidth }] = useElementSize<HTMLButtonElement>();
  const [pendingDeleteAcademic, setPendingDeleteAcademic] = useState<{ id: number } | null>(null);
  const sendToastAssignUC = useHandleSuccess({ title: 'Assign Unit Coordinator' });
  const sendToastAssignStaff = useHandleSuccess({ title: 'Assign Staff' });

  const { isLoading: isUpdatingUC, mutate: updateUC } = trpc.updateUnitWorkloadUC.useMutation({
    onSuccess: async () => {
      await handleInvalidateAndRefetch(unitWorkloadId);
      sendToastAssignUC();
    },
    onError: (error) => {
      console.error('Error updating UC:', error);
      handleError([error]);
    },
  });

  const { data: academicList } = useSearchAcademic(searchTerm, activeYear);
  const trpcUtils = trpc.useUtils();
  // Every invalidate below is scoped to this unit workload. invalidate() already refetches the
  // queries mounted on this page and marks the rest stale so they reload when their own page is
  // next opened
  const handleInvalidateAndRefetch = async (unitWorkloadId: number) => {
    await trpcUtils.getAssignedAcademicsForUnitWorkload.invalidate({ unitWorkloadId });
    if (selectedCampusId > 0) {
      await trpcUtils.getTeachingDeliveryDetails.invalidate({ unitWorkloadId, campusId: selectedCampusId });
    }
    if (selectedSchoolId > 0) {
      await trpcUtils.schoolUnit.getSchoolUnitDetails.invalidate({ schoolId: selectedSchoolId, year: activeYear });
    }
    await trpcUtils.trd.getUnitWorkloadTeachingHours.invalidate({
      unitWorkloadId,
      campusId: selectedCampusId > 0 ? selectedCampusId : undefined,
    });
    await trpcUtils.trd.getAcademicDutyHours.invalidate({
      unitWorkloadId,
      campusId: selectedCampusId > 0 ? selectedCampusId : undefined,
    });
    // Note: schoolId is deliberately omitted. This query is keyed on the unit workload's own school
    // (use-unit-workload-data passes unitWorkloadSchool.id), which is not necessarily the school
    // selected in the store. Omitting it keeps the filter a subset that matches either value. Devs: monitor the effect of this change.
    await trpcUtils.unit.getUnitWorkloadDetails.invalidate({ unitWorkloadId, year: activeYear });
  };

  const { isLoading: isAdding, mutate: addAcademicAssignmentMutation } = trpc.addUnitWorkloadAcademic.useMutation({
    onSuccess: async () => {
      await handleInvalidateAndRefetch(unitWorkloadId);
      sendToastAssignStaff();
    },
    onError: (error) => {
      console.error('Error adding academic assignment:', error);
      handleError([error]);
    },
  });

  const { isLoading: isDeleting, mutate: removeAcademicAssignmentMutation } =
    trpc.removeUnitWorkloadAcademic.useMutation({
      onSuccess: async () => {
        await handleInvalidateAndRefetch(unitWorkloadId);
      },
      onError: (error) => {
        console.error('Error removing academic assignment:', error);
        handleError([error]);
      },
    });

  const isMutationLoading = isAdding || isDeleting || isUpdatingUC;

  const isCurtinIdExistsInAssignedList = (curtinId: string) =>
    assignedAcademics?.some((academic) => academic.academicWorkload.curtinId === curtinId);

  const addAcademicAssignment = (academic: any): void => {
    if (!unitWorkloadId) return;
    addAcademicAssignmentMutation({ unitWorkloadId: unitWorkloadId, academicId: academic.id });
    setOpen(false);
  };

  const removeAcademicAssignment = ({ id }: { id: number }): void => {
    if (unitWorkloadId !== null) {
      removeAcademicAssignmentMutation({ unitWorkloadId: unitWorkloadId, academicId: id });
    }
  };

  const updateSearchTerm = (commandInputValue: string): void => {
    if (commandInputValue.trim().length > 1) {
      setSearchTerm(commandInputValue);
    } else {
      setSearchTerm('');
    }
    setCommandInputValue(commandInputValue);
  };

  const handleUCClick = (staffId: string) => {
    const academic = assignedAcademics?.find((a) => a.academicWorkload.curtinId === staffId);
    updateUC({
      unitWorkloadId,
      id: academic?.academicWorkload.id || 0,
      curtinId: academic?.academicWorkload.curtinId || '',
      firstName: academic?.academicWorkload.firstName || '',
      lastName: academic?.academicWorkload.lastName || '',
      employmentStatusAbbr: academic?.academicWorkload.employmentStatusAbbr || '',
    });
  };

  return (
    <>
      {isMutationLoading && <Loader />}

      <AlertModal
        title="Removing assigned staff"
        description="This will permanently remove the assigned staff from the allocated unit."
        confirmAction={() => {
          setAlert(false);
          if (pendingDeleteAcademic) {
            removeAcademicAssignment(pendingDeleteAcademic);
          }
        }}
        open={alert}
        cancelAction={() => {
          setAlert(false);
        }}
      />

      <AlertModal
        title="Cannot remove assigned staff"
        description="Cannot remove the assigned staff because they are the unit coordinator. Please assign a new coordinator before removing."
        confirmLabel="OK"
        confirmAction={() => {
          setCannotRemoveAlert(false);
        }}
        open={cannotRemoveAlert}
      />

      <Card className="pt-4.5 w-full min-w-[620px] grow basis-3/5 overflow-auto bg-white pb-1 shadow-none">
        <CardHeader>
          <CardTitle className="mb-3 text-lg">Assign staff</CardTitle>
          <CardDescription className="text-base">
            Search for staff and add them to the assigned staff by clicking their name on the list.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex w-[80%] flex-row justify-between">
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild ref={triggerRef}>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className={`h-10 w-[320px] justify-between rounded-lg border-[1px] border-input bg-white px-3 py-5 text-base font-normal leading-6
                    text-gray-600 hover:border-deep-blue focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-deep-blue
                    ${open ? 'ring-1 ring-deep-blue' : ''}`}
                  disabled={unitWorkloadCampuses?.length === 0}
                >
                  Add staff
                  <ChevronIcon
                    className="h-4 w-4 fill-[#000000] transition-transform duration-300"
                    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0" style={{ width: `${triggerWidth}px` }}>
                <Command className="bg-white">
                  <CommandInput
                    placeholder="Search staff by id or name"
                    aria-label="Search staff by id or name"
                    className="h-10 w-[447px] bg-white px-3 pl-5 text-base font-normal leading-6 placeholder:text-secondary-grey-01
                      hover:border-deep-blue focus-visible:ring-deep-blue"
                    value={commandInputValue}
                    onValueChange={updateSearchTerm}
                  />
                  <CommandList>
                    <CommandEmpty className="p-5 text-base text-secondary-grey-01">
                      {searchTerm === '' ? 'Type at least 2 characters to search' : 'No results found.'}
                    </CommandEmpty>
                    <CommandGroup>
                      {academicList?.map((academic: any) => (
                        <CommandItem
                          key={academic.id}
                          value={`${academic.lastName}, ${academic.firstName} (${academic.curtinId})`}
                          onSelect={() => addAcademicAssignment(academic)}
                          className="disabled py-[10px] text-base font-semibold focus:text-deep-blue data-[selected=true]:text-deep-blue"
                          disabled={isCurtinIdExistsInAssignedList(academic.curtinId)}
                        >
                          {academic.lastName}, {academic.firstName} ({academic.curtinId})
                          <Check
                            className={cn(
                              'ml-auto',
                              isCurtinIdExistsInAssignedList(academic.curtinId) ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          {assignedAcademics && assignedAcademics.length > 0 && (
            <div className="mr-2 flex flex-row justify-end">UC</div>
          )}
          <div className="space-y-2">
            {assignedAcademics && (
              <RadioGroup>
                {assignedAcademics.map(({ academicWorkload: { id, curtinId, firstName, lastName } }) => (
                  <div
                    key={id}
                    className="flex-start flex items-center justify-between rounded-md border-b border-secondary-grey-04 p-2"
                  >
                    <p className="flex w-[60%] justify-start text-base font-semibold">
                      {lastName}, {firstName}
                    </p>
                    <p className="w-[10%] text-sm">{curtinId}</p>

                    <DeleteButton
                      label="Remove staff member"
                      onClick={() => {
                        if (curtinId === unitWorkloadDetails?.coordinatorCurtinId) {
                          setCannotRemoveAlert(true);
                          return;
                        }
                        setPendingDeleteAcademic({ id });
                        setAlert(true);
                      }}
                    />

                    <div className="flex items-center justify-center">
                      <RadioGroupItem
                        onClick={() => handleUCClick(curtinId)}
                        key={id}
                        value={curtinId}
                        id={curtinId}
                        checked={curtinId === unitWorkloadDetails?.coordinatorCurtinId}
                      ></RadioGroupItem>
                    </div>
                  </div>
                ))}
              </RadioGroup>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  );
};
